import React, { useState } from 'react';
import {
  useCareOrders,
  useCreateCareOrder,
  useAcknowledgeCareOrder,
  useCompleteCareOrder,
  useBlockCareOrder,
  useUpdateCareOrder,
  useApproveCareOrderStep,
  useRejectCareOrderStep,
  useCreateCareOrderHandoff,
  useAcceptCareOrderHandoff,
} from '@/services/api';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';
import {
  ClockIcon,
  CheckCircleIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import CareOrderHistoryModal from './CareOrderHistoryModal';
import Modal from './Modal';

interface PatientOrdersProps {
  patientId: string | number;
}

const formatTime = (value?: string | null) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const PatientOrders: React.FC<PatientOrdersProps> = ({ patientId }) => {
  const { hasPermission } = useAuth();
  const { pushToast } = useToasts();

  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [historyOrderId, setHistoryOrderId] = useState<string | null>(null);
  const [signoffDialog, setSignoffDialog] = useState<{ orderId: string; step: number; mode: 'approve' | 'reject'; label: string } | null>(null);
  const [signoffNote, setSignoffNote] = useState('');
  const [signoffAssignee, setSignoffAssignee] = useState('');
  const [handoffDialog, setHandoffDialog] = useState<{ orderId: string; suggestedRole?: string } | null>(null);
  const [handoffNote, setHandoffNote] = useState('');
  const [handoffRole, setHandoffRole] = useState('');

  const [orderForm, setOrderForm] = useState({
    category: 'medication' as 'medication' | 'lab' | 'nursing' | 'followup',
    title: '',
    details: '',
    priority: 'Routine' as 'Critical' | 'High' | 'Routine',
    assignedRole: 'RN',
    assignedTo: '',
    dueAt: '',
  });

  const filters = {
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
  };

  const {
    data: orders = [],
    isLoading,
    isFallback,
    fallbackMessage,
  } = useCareOrders(patientId, filters);

  const createOrder = useCreateCareOrder();
  const acknowledgeOrder = useAcknowledgeCareOrder();
  const completeOrder = useCompleteCareOrder();
  const blockOrder = useBlockCareOrder();
  const updateOrder = useUpdateCareOrder();
  const approveSignoff = useApproveCareOrderStep();
  const rejectSignoff = useRejectCareOrderStep();
  const createHandoff = useCreateCareOrderHandoff();
  const acceptHandoff = useAcceptCareOrderHandoff();

  const hasSignoffPermission = hasPermission('care:signoff');
  const hasHandoffPermission = hasPermission('care:handoff');

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasPermission('care:update')) {
      pushToast({ tone: 'error', message: '無權限建立指示' });
      return;
    }
    if (!orderForm.title || !orderForm.details) {
      pushToast({ tone: 'error', message: '請填寫標題和詳細內容' });
      return;
    }
    try {
      await createOrder.mutateAsync({
        patientId,
        ...orderForm,
        dueAt: orderForm.dueAt || undefined,
        assignedTo: orderForm.assignedTo || undefined,
      });
      pushToast({ tone: 'success', message: '指示已建立' });
      setOrderForm({
        category: 'medication',
        title: '',
        details: '',
        priority: 'Routine',
        assignedRole: 'RN',
        assignedTo: '',
        dueAt: '',
      });
      setShowForm(false);
    } catch (err) {
      pushToast({ tone: 'error', message: '建立失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleAcknowledge = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限接單' });
      return;
    }
    try {
      await acknowledgeOrder.mutateAsync(orderId);
      pushToast({ tone: 'success', message: '已接單' });
    } catch (err) {
      pushToast({ tone: 'error', message: '接單失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleComplete = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限完成指示' });
      return;
    }
    const notes = prompt('完成備註（選填）：');
    if (notes === null) return;
    try {
      await completeOrder.mutateAsync({ orderId, notes: notes || undefined });
      pushToast({ tone: 'success', message: '指示已完成' });
    } catch (err) {
      pushToast({ tone: 'error', message: '完成失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleInProgress = async (orderId: string) => {
    if (!hasPermission('care:execute')) {
      pushToast({ tone: 'error', message: '無權限更新狀態' });
      return;
    }
    try {
      await updateOrder.mutateAsync({ orderId, payload: { status: 'in_progress' } });
      pushToast({ tone: 'success', message: '已開始執行' });
    } catch (err) {
      pushToast({ tone: 'error', message: '更新失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleBlock = async (orderId: string) => {
    if (!hasPermission('care:update')) {
      pushToast({ tone: 'error', message: '無權限封鎖指示' });
      return;
    }
    const reason = prompt('封鎖原因：');
    if (reason === null || reason.trim() === '') return;
    try {
      await blockOrder.mutateAsync({ orderId, reason });
      pushToast({ tone: 'success', message: '指示已封鎖' });
    } catch (err) {
      pushToast({ tone: 'error', message: '封鎖失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleUnblock = async (orderId: string) => {
    if (!hasPermission('care:update')) {
      pushToast({ tone: 'error', message: '無權限解除封鎖' });
      return;
    }
    try {
      await updateOrder.mutateAsync({ orderId, payload: { status: 'open', blockReason: null } });
      pushToast({ tone: 'success', message: '指示已解除封鎖' });
    } catch (err) {
      pushToast({ tone: 'error', message: '解除失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const openSignoffModal = (
    orderId: string,
    step: number,
    label: string,
    mode: 'approve' | 'reject',
    currentAssignee?: string,
  ) => {
    setSignoffDialog({ orderId, step, mode, label });
    setSignoffAssignee(currentAssignee ?? '');
    setSignoffNote('');
  };

  const handleSignoffSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!signoffDialog) return;
    try {
      if (signoffDialog.mode === 'approve') {
        await approveSignoff.mutateAsync({
          orderId: signoffDialog.orderId,
          step: signoffDialog.step,
          payload: {
            note: signoffNote.trim() || undefined,
            assignee: signoffAssignee.trim() || undefined,
          },
        });
        pushToast({ tone: 'success', message: '簽核完成' });
      } else {
        if (!signoffNote.trim()) {
          pushToast({ tone: 'error', message: '請輸入退回原因' });
          return;
        }
        await rejectSignoff.mutateAsync({
          orderId: signoffDialog.orderId,
          step: signoffDialog.step,
          payload: {
            note: signoffNote.trim(),
            assignee: signoffAssignee.trim() || undefined,
          },
        });
        pushToast({ tone: 'success', message: '已退回上一階段' });
      }
      setSignoffDialog(null);
      setSignoffAssignee('');
      setSignoffNote('');
    } catch (err) {
      pushToast({ tone: 'error', message: '簽核流程失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const openHandoffModal = (orderId: string, suggestedRole?: string) => {
    setHandoffDialog({ orderId, suggestedRole });
    setHandoffRole(suggestedRole ?? '');
    setHandoffNote('');
  };

  const handleHandoffSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!handoffDialog) return;
    if (!handoffNote.trim()) {
      pushToast({ tone: 'error', message: '請輸入交班備註' });
      return;
    }
    try {
      await createHandoff.mutateAsync({
        orderId: handoffDialog.orderId,
        payload: { note: handoffNote.trim(), toRole: handoffRole.trim() || undefined },
      });
      pushToast({ tone: 'success', message: '交班已建立' });
      setHandoffDialog(null);
      setHandoffNote('');
      setHandoffRole('');
    } catch (err) {
      pushToast({ tone: 'error', message: '交班建立失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleAcceptHandoff = async (orderId: string, handoffId?: string) => {
    try {
      await acceptHandoff.mutateAsync({ orderId, handoffId });
      pushToast({ tone: 'success', message: '已接班' });
    } catch (err) {
      pushToast({ tone: 'error', message: '接班失敗', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'medication': return 'bg-blue-100 text-blue-700';
      case 'lab': return 'bg-purple-100 text-purple-700';
      case 'nursing': return 'bg-emerald-100 text-emerald-700';
      case 'followup': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Routine': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-50 text-blue-700';
      case 'acknowledged': return 'bg-cyan-50 text-cyan-700';
      case 'in_progress': return 'bg-yellow-50 text-yellow-700';
      case 'done': return 'bg-emerald-50 text-emerald-700';
      case 'blocked': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const getSignoffBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'pending':
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const formatSignoffStatus = (status: string) => {
    if (status === 'approved') return '已簽核';
    if (status === 'rejected') return '已退回';
    return '待簽核';
  };

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-900">Care Orders</h3>
          {isFallback && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
              Cached{fallbackMessage ? ` · ${fallbackMessage}` : ''}
            </span>
          )}
        </div>
        {hasPermission('care:update') && (
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-1 text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            {showForm ? '取消' : '新增指示'}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select
          className="input text-xs"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="all">所有類別</option>
          <option value="medication">給藥</option>
          <option value="lab">檢驗</option>
          <option value="nursing">護理</option>
          <option value="followup">追蹤</option>
        </select>
        <select
          className="input text-xs"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="all">所有狀態</option>
          <option value="open">待接單</option>
          <option value="acknowledged">已接單</option>
          <option value="in_progress">執行中</option>
          <option value="done">已完成</option>
          <option value="blocked">已封鎖</option>
        </select>
      </div>

      {/* Create Form */}
      {showForm && hasPermission('care:update') && (
        <form onSubmit={handleCreateOrder} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">類別</label>
              <select
                className="input text-sm"
                value={orderForm.category}
                onChange={(e) => setOrderForm({ ...orderForm, category: e.target.value as any })}
              >
                <option value="medication">給藥</option>
                <option value="lab">檢驗</option>
                <option value="nursing">護理</option>
                <option value="followup">追蹤</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">優先級</label>
              <select
                className="input text-sm"
                value={orderForm.priority}
                onChange={(e) => setOrderForm({ ...orderForm, priority: e.target.value as any })}
              >
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Routine">Routine</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">標題</label>
            <input
              type="text"
              className="input text-sm"
              value={orderForm.title}
              onChange={(e) => setOrderForm({ ...orderForm, title: e.target.value })}
              placeholder="例：Prednisone taper"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">詳細內容</label>
            <textarea
              className="input text-sm min-h-[80px]"
              value={orderForm.details}
              onChange={(e) => setOrderForm({ ...orderForm, details: e.target.value })}
              placeholder="完整指示內容、檢驗項目、護理動作等"
              required
            />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">指派角色</label>
              <select
                className="input text-sm"
                value={orderForm.assignedRole}
                onChange={(e) => setOrderForm({ ...orderForm, assignedRole: e.target.value })}
              >
                <option value="RN">RN</option>
                <option value="ICU tech">ICU tech</option>
                <option value="Lab">Lab</option>
                <option value="Front desk">Front desk</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">指派人員（選填）</label>
              <input
                type="text"
                className="input text-sm"
                value={orderForm.assignedTo}
                onChange={(e) => setOrderForm({ ...orderForm, assignedTo: e.target.value })}
                placeholder="選填"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">到期時間（選填）</label>
              <input
                type="datetime-local"
                className="input text-sm"
                value={orderForm.dueAt}
                onChange={(e) => setOrderForm({ ...orderForm, dueAt: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-secondary text-sm"
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={createOrder.isPending}
            >
              {createOrder.isPending ? '建立中...' : '建立指示'}
            </button>
          </div>
        </form>
      )}

      {/* Orders List */}
      {isLoading ? (
        <p className="text-sm text-slate-500">載入中...</p>
      ) : orders.length === 0 ? (
        <p className="text-sm text-slate-500">目前沒有指示</p>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => {
            const signoffs = order.signoffs ?? [];
            const currentStep = signoffs.find((step) => step.status !== 'approved');
            const pendingHandoff = order.pendingHandoff ?? null;
            const latestHandoff = order.latestHandoff ?? null;
            const slaBadgeMinutes = signoffs.find((step) => step.slaMinutes)?.slaMinutes;
            return (
              <div
                key={order.id}
                className="rounded-lg border border-slate-200 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div>
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`pill text-[10px] ${getCategoryColor(order.category)}`}>
                          {order.category}
                        </span>
                        <span className={`pill text-[10px] ${getPriorityColor(order.priority)}`}>
                          {order.priority}
                        </span>
                        <span className={`pill text-[10px] ${getStatusColor(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                        {slaBadgeMinutes && (
                          <span
                            className={`pill text-[10px] ${
                              order.slaBreached ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {order.slaBreached ? 'SLA 逾時' : `SLA ${slaBadgeMinutes}m`}
                          </span>
                        )}
                      </div>
                      <h4 className="mb-1 font-semibold text-slate-900">{order.title}</h4>
                      <p className="mb-2 text-sm text-slate-600">{order.details}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>{order.assignedRole}</span>
                        {order.assignedTo && <span>→ {order.assignedTo}</span>}
                        {order.dueAt && (
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {formatTime(order.dueAt)}
                          </span>
                        )}
                        <span>v{order.version}</span>
                      </div>
                    </div>

                    {signoffs.length > 0 && (
                      <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600">
                          <span>簽核進度</span>
                          {order.slaBreached && <span className="text-red-600">優先處理</span>}
                        </div>
                        <ol className="space-y-2">
                          {signoffs.map((step) => {
                            const actionable = hasSignoffPermission && currentStep && currentStep.step === step.step && step.status !== 'approved';
                            return (
                              <li
                                key={`${order.id}-${step.step}`}
                                className="rounded-lg bg-white/80 p-2 shadow-sm"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                                      Step {step.step}
                                    </span>
                                    <span>{step.label}</span>
                                    {step.slaMinutes && (
                                      <span
                                        className={`pill text-[10px] ${
                                          step.slaBreached ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                        }`}
                                      >
                                        {step.slaBreached ? '逾期' : `SLA ${step.slaMinutes}m`}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] ${getSignoffBadgeColor(step.status)}`}>
                                      {formatSignoffStatus(step.status)}
                                    </span>
                                    {actionable && (
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            openSignoffModal(order.id, step.step, step.label, 'approve', step.assignee ?? undefined)
                                          }
                                          className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                                          disabled={approveSignoff.isPending}
                                        >
                                          簽核
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            openSignoffModal(order.id, step.step, step.label, 'reject', step.assignee ?? undefined)
                                          }
                                          className="rounded bg-red-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                                          disabled={rejectSignoff.isPending}
                                        >
                                          退回
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <p className="text-[11px] text-slate-500">指派：{step.assignee || '尚未指派'}</p>
                                {step.note && <p className="text-[11px] text-slate-500">備註：{step.note}</p>}
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}

                    <div className="grid gap-3 text-xs text-slate-600 md:grid-cols-2">
                      <div className="rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between text-[11px] font-semibold">
                          <span>交班狀態</span>
                          <span className={pendingHandoff ? 'text-amber-600' : latestHandoff ? 'text-emerald-600' : 'text-slate-400'}>
                            {pendingHandoff ? '待接班' : latestHandoff ? '已接班' : '尚未交班'}
                          </span>
                        </div>
                        {latestHandoff ? (
                          <p className="mt-1 text-[11px]">
                            {latestHandoff.fromRole} → {latestHandoff.toRole}
                          </p>
                        ) : (
                          <p className="mt-1 text-[11px] text-slate-500">尚無交班紀錄</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {hasHandoffPermission && (
                            <button
                              type="button"
                              onClick={() => openHandoffModal(order.id, order.assignedRole ?? latestHandoff?.toRole ?? undefined)}
                              className="btn-secondary text-[11px]"
                              disabled={createHandoff.isPending}
                            >
                              {pendingHandoff ? '重新交班' : '建立交班'}
                            </button>
                          )}
                          {pendingHandoff && hasHandoffPermission && (
                            <button
                              type="button"
                              onClick={() => handleAcceptHandoff(order.id, pendingHandoff.id)}
                              className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                              disabled={acceptHandoff.isPending}
                            >
                              接班確認
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[11px] font-semibold">最新交班備註</p>
                        {latestHandoff ? (
                          <>
                            <p className="mt-1 text-[11px] text-slate-500">{latestHandoff.handoffNote || '—'}</p>
                            <p className="text-[11px] text-slate-400">{formatTime(latestHandoff.createdAt)}</p>
                          </>
                        ) : (
                          <p className="mt-1 text-[11px] text-slate-500">尚未交班</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setHistoryOrderId(order.id)}
                      className="btn-secondary text-xs px-2 py-1"
                    >
                      📋 歷史
                    </button>
                    {order.status === 'open' && hasPermission('care:execute') && (
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(order.id)}
                        className="btn-secondary text-xs px-2 py-1"
                        disabled={acknowledgeOrder.isPending}
                      >
                        接單
                      </button>
                    )}
                    {order.status === 'acknowledged' && hasPermission('care:execute') && (
                      <button
                        type="button"
                        onClick={() => handleInProgress(order.id)}
                        className="btn-primary text-xs px-2 py-1"
                        disabled={updateOrder.isPending}
                      >
                        開始
                      </button>
                    )}
                    {(order.status === 'acknowledged' || order.status === 'in_progress') && hasPermission('care:execute') && (
                      <button
                        type="button"
                        onClick={() => handleComplete(order.id)}
                        className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                        disabled={completeOrder.isPending}
                      >
                        <CheckCircleIcon className="h-3 w-3" />
                        完成
                      </button>
                    )}
                    {order.status !== 'blocked' && hasPermission('care:update') && (
                      <button
                        type="button"
                        onClick={() => handleBlock(order.id)}
                        className="flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                        disabled={blockOrder.isPending}
                      >
                        <XCircleIcon className="h-3 w-3" />
                        封鎖
                      </button>
                    )}
                    {order.status === 'blocked' && hasPermission('care:update') && (
                      <button
                        type="button"
                        onClick={() => handleUnblock(order.id)}
                        className="btn-secondary text-xs px-2 py-1"
                        disabled={updateOrder.isPending}
                      >
                        解除
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Signoff Modal */}
      {signoffDialog && (
        <Modal
          isOpen={!!signoffDialog}
          onClose={() => setSignoffDialog(null)}
          title={`${signoffDialog.mode === 'approve' ? '簽核' : '退回'}：${signoffDialog.label}`}
          size="md"
        >
          <form onSubmit={handleSignoffSubmit} className="space-y-4 p-1">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">簽核意見</label>
              <textarea
                className="input text-sm min-h-[90px]"
                value={signoffNote}
                onChange={(e) => setSignoffNote(e.target.value)}
                placeholder={signoffDialog.mode === 'approve' ? '選填' : '退回原因必填'}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">自訂負責人（選填）</label>
              <input
                className="input text-sm"
                value={signoffAssignee}
                onChange={(e) => setSignoffAssignee(e.target.value)}
                placeholder="例：RN 王小美"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={() => setSignoffDialog(null)}>
                取消
              </button>
              <button
                type="submit"
                className="btn-primary text-sm"
                disabled={approveSignoff.isPending || rejectSignoff.isPending}
              >
                {signoffDialog.mode === 'approve'
                  ? approveSignoff.isPending
                    ? '簽核中...'
                    : '確認簽核'
                  : rejectSignoff.isPending
                    ? '退回中...'
                    : '退回'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Handoff Modal */}
      {handoffDialog && (
        <Modal
          isOpen={!!handoffDialog}
          onClose={() => setHandoffDialog(null)}
          title="交班資訊"
          size="md"
        >
          <form onSubmit={handleHandoffSubmit} className="space-y-4 p-1">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">交班對象角色</label>
              <input
                className="input text-sm"
                value={handoffRole}
                onChange={(e) => setHandoffRole(e.target.value)}
                placeholder={handoffDialog.suggestedRole || '例：ICU / 手術 / RN'}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">交班備註</label>
              <textarea
                className="input text-sm min-h-[90px]"
                value={handoffNote}
                onChange={(e) => setHandoffNote(e.target.value)}
                placeholder="描述交班內容、重點或資源需求"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={() => setHandoffDialog(null)}>
                取消
              </button>
              <button
                type="submit"
                className="btn-primary text-sm"
                disabled={createHandoff.isPending}
              >
                {createHandoff.isPending ? '送出中...' : '建立交班'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* History Modal */}
      {historyOrderId && (
        <CareOrderHistoryModal
          orderId={historyOrderId}
          isOpen={!!historyOrderId}
          onClose={() => setHistoryOrderId(null)}
        />
      )}
    </div>
  );
};

export default PatientOrders;
